import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Copy } from "lucide-react";
import ProductWizard from "@/components/ProductWizard";

export default function VendorProducts() {
  const { vendor } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => setProducts(await api.get("/vendor/products").then(r => r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const clone = async (id) => {
    try {
      await api.post(`/products/${id}/clone`);
      toast.success("Duplicated — edit the copy; it goes for inspection like any new listing");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Duplicate failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Catalogue</p>
          <h1 className="font-serif text-6xl mt-2 tracking-tighter">Products.</h1>
        </div>
        {vendor.status === "approved" && (
          <button data-testid="new-product-btn" onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] flex items-center gap-2">
            <Plus size={14} /> New Product
          </button>
        )}
      </div>

      {products.length === 0 ? (
        <p className="font-serif text-2xl text-muted-foreground">No products yet.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="text-left py-2">Item</th><th className="text-left">Category</th><th className="text-left">Price</th><th className="text-left">Stock</th><th className="text-left">Inspection</th><th></th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id} className="border-b border-foreground/20">
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-secondary overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <p className="font-serif">{p.title}</p>
                      <p className="text-[11px] font-mono text-muted-foreground">{p.brand}</p>
                    </div>
                  </div>
                </td>
                <td className="capitalize">{p.subcategory_name || p.category_name || p.category}</td>
                <td>₹ {p.price.toLocaleString("en-IN")}</td>
                <td>{p.stock}</td>
                <td>
                  {p.approved ? <span className="text-[hsl(var(--accent))] font-mono text-xs">SPOTTED</span>
                    : p.spotted_certificate ? <span className="text-destructive font-mono text-xs">FAILED</span>
                    : <span className="font-mono text-xs text-muted-foreground">PENDING</span>}
                </td>
                <td className="text-right">
                  <button data-testid={`edit-${p.id}`} onClick={() => { setEditing(p); setShowForm(true); }} className="text-xs font-mono uppercase hover:text-[hsl(var(--accent))] mr-4">Edit</button>
                  <button data-testid={`clone-${p.id}`} onClick={() => clone(p.id)} title="Duplicate product" className="text-xs font-mono uppercase hover:text-[hsl(var(--accent))] mr-4 inline-flex items-center gap-1"><Copy size={13} /> Duplicate</button>
                  <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="hover:text-destructive"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {showForm && <ProductWizard initial={editing} onSaved={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
    </div>
  );
}
